/**
 * 预订转入住
 */
import React, { useEffect, useRef, memo, useContext } from 'react';
import { connect } from 'react-redux';
import { message, Spin, Button } from 'antd';
import intl from 'react-intl-universal';

import usePortalDrawer, { DrawerHeader, DrawerFooter } from '@/components/PortalDrawer';
import useConfirmModal from '@/components/PortalConfirm';
import PortalLoading from '@/components/PortalLoading';
import PhoneCheckModal, { PhoneCheckModalRef } from '@/components/PhoneCheckModal';
import checkGuestConflict from '@/pages/reservation/checkIn/components/GuestConflict';
import PreSettleDrawer, { PreSettleDrawerRef } from '@/components/Reservation/PreSettleDrawer';
import PopupTarget, { TargetType } from '@/components/Reservation/PopupTarget';
import {
  showWardRoundConfirm,
  showLeaveConfirm,
  showOutdateConfirm,
  showJuvenilesConfirm,
  showSourceAddressAlert,
} from '@/components/Reservation/Modal';
import { OrderDetailTabKey } from '@/pages/order/detail/component/DetailInfo';
import checkInService from '@/api/checkIn';
import { MemberType } from '@/config/constant';
import showGamePromptModal from '@/components/Reservation/GamePromptModal';

import { createStateSelector } from '@/redux/actions/reservation/helper';
import {
  initPageState,
  clearPageState,
  setLoadingStatus,
  getMainState,
  batchUpdateGuestMobile,
  refreshOutdateRoomInfo,
} from '@/redux/actions/reservation/checkInByReserve';
import { ReducerState, storeName } from '@/redux/reducers/reservation/checkInByReserve';
import ThrottleButton from '@/components/Reservation/ThrottleButton';
import useOrderLink from '@/hooks/useOrderLink';
import IntlContext from '@/components/Intl/IntlContext';

import Notice from './components/CardReadNotice';
import CustomerList from './components/CustomerInfo';
import RentTimeInfoNode from './components/RentTimeInfo';
import ChannelInfoNode from './components/ChannelInfo';
import RoomSelector from './components/RoomSelector';
import RemarkInfo from './components/RemarkInfo';
import validator from './validator';
import * as helper from './helper';

import styles from './index.module.scss';

const stateSelector = (state: ReducerState) => {
  const { isInit, isLoading, commonInfo } = state;
  return { isInit, isLoading, isAppendMode: commonInfo.isAppendMode };
};
const mapStateToProps = createStateSelector<ReducerState>(storeName, stateSelector);
const mapDispatchToProps = {
  callInitState: initPageState,
  callClearState: clearPageState,
  callSetLoading: setLoadingStatus,
  callGetState: getMainState,
  callRefreshRoomInfo: refreshOutdateRoomInfo,
  callBatchUpdateMobile: batchUpdateGuestMobile,
};

type MapStateProps = ReturnType<typeof stateSelector>;
type MapDispatchProps = typeof mapDispatchToProps;

interface IConfig {
  mainOrderId: string; // 订单id
  recList: Array<string>; // 接待单列表
  onEnd?: (result: boolean) => void;
}
interface IProps extends IConfig, MapStateProps, MapDispatchProps {
  unmountNode: () => void;
}

const Index: React.FC<IProps> = (props) => {
  const {
    unmountNode,
    onEnd,
    isInit,
    isLoading,
    isAppendMode,
    callSetLoading,
    callGetState,
    callClearState,
  } = props;

  const confirmPortal = useConfirmModal();
  const phoneCheckModalRef = useRef<PhoneCheckModalRef>();
  const preSettleRef = useRef<PreSettleDrawerRef>(null);
  const { linkToReservationDetail } = useOrderLink();
  const intlCtx = useContext(IntlContext);

  const closeDrawer = () => {
    unmountNode();
    callClearState();
  };

  useEffect(() => {
    const { callInitState, mainOrderId, recList } = props;
    if (mainOrderId && recList && recList.length) {
      // 初始化数据
      callInitState(() => {
        return helper.getStateFromOrder(mainOrderId, recList).catch((e) => {
          closeDrawer();
          throw e;
        });
      });
    } else {
      closeDrawer();
      message.error(intl.get('reservation_error_config_invalid'));
    }
  }, []);

  // 取消
  const handleEditCancel = async () => {
    callGetState(async (state) => {
      const { isModify } = state;
      // 数据有变动
      if (isModify) {
        const isConfirm = await showLeaveConfirm(confirmPortal); // 是否确认离开
        if (!isConfirm) return;
        closeDrawer();
      } else {
        closeDrawer();
      }
    });
  };

  // 未成年人检测
  const showJuvenilesCheck: (grownUpCount: number, unGrownUpCount: number) => Promise<void> = (
    grownUpCount,
    unGrownUpCount,
  ) => {
    return showJuvenilesConfirm(confirmPortal, { grownUpCount, unGrownUpCount });
  };

  // 手机号冲突检测
  const showMobileConflictCheck: () => Promise<void> = async () => {
    return new Promise((resolve, reject) => {
      callGetState((state) => {
        let list = state.allGuestList || [];
        // 预订转追加入住房主不参与
        if (isAppendMode) {
          list = list?.filter((item) => item.memberType !== MemberType.HouseOwner);
        }
        const inst = phoneCheckModalRef.current;
        if (inst) {
          inst.showPhoneCheck(list).then((res) => {
            if (res.success) {
              resolve();
            } else {
              reject();
            }
          });
        }
      });
    });
  };

  // 预结算
  const showPreSettleDrawer = (orderNo) => {
    if (orderNo) {
      preSettleRef.current.showDrawer(orderNo, () => {
        message.success(intl.get('drawer_checkin_message_order_create_success'));
        if (isAppendMode) {
          // 追加入住
          if (onEnd) {
            onEnd(true);
          }
        } else {
          // 新入住
          linkToReservationDetail(orderNo, {
            tabKey: OrderDetailTabKey.RoomKey,
          });
        }
        closeDrawer();
      });
    }
  };

  // 房间状态变动 需要重新刷新房间数据
  const handleRefreshErr: (response) => void = async (response) => {
    // 房间变动
    await showOutdateConfirm(confirmPortal, response, 'reserve').then((shouldRefresh) => {
      if (shouldRefresh) {
        const { callRefreshRoomInfo } = props;
        callRefreshRoomInfo();
      }
    });
  };

  // 校验是否未查房
  const handleRoomCheckErr: (response) => void = async (response) => {
    await showWardRoundConfirm(confirmPortal, response, 'checkIn');
  };

  // 校验数据
  const preCheckData = async () => {
    await new Promise<void>((resolve, reject) => {
      callGetState(async (state) => {
        try {
          const err = validator(state);
          if (err) {
            message.error(err);
            reject();
            return;
          }
          const guestList = state.allGuestList || [];
          await checkGuestConflict(
            confirmPortal,
            // 追加场景下 不校验房主
            isAppendMode
              ? guestList.filter((item) => item.memberType !== MemberType.HouseOwner)
              : guestList,
          );
          await showMobileConflictCheck();
          resolve();
        } catch (e) {
          reject();
        }
      });
    });
  };

  // 宾客住址检测
  const checkSourceAddress = () => {
    return new Promise((resolve) => {
      callGetState((state) => {
        showSourceAddressAlert(confirmPortal, state.allGuestList).then(resolve, resolve);
      });
    });
  };

  // 提交数据
  const submitFormData = async () => {
    await new Promise<void>((resolve, reject) => {
      callGetState(async (state) => {
        try {
          const requestData = helper.formatToSubmitData(state);
          await checkInService.checkCheckInReserve(requestData).then(
            async (res) => {
              const { grownUpCount, unGrownUpCount } = res?.data || {};
              if (intlCtx.TenantSysConfig.juvenilesValidate) {
                await showJuvenilesCheck(grownUpCount, unGrownUpCount);
              }
            },
            async (errRes) => {
              await handleRefreshErr(errRes);
              await handleRoomCheckErr(errRes);
              throw errRes;
            },
          );
          if (intlCtx.TenantSysConfig.nationValidate) {
            await checkSourceAddress();
          }
          await checkInService.submitCheckInByReserve(requestData).then(
            (res) => {
              showPreSettleDrawer(res.data);
              resolve();
            },
            async (errRes) => {
              await handleRefreshErr(errRes);
              throw errRes;
            },
          );
          // await showPrepareConfirm(checkRoomUnFinished);
          // const isToCard = await showRoomCardConfirm(confirmPortal);
          // if (isToCard) {
          //   //  去制卡
          // }
        } catch (e) {
          reject();
        }
      });
    });
  };

  // 校验是否有报名赛事
  const checkYGPGame = () => {
    callGetState((state) => {
      const guestList = state.allGuestList || [];
      showGamePromptModal(guestList.map((item) => ({ wyuId: item.wyuId })));
    });
  };

  const handleSubmitClick = async () => {
    try {
      callSetLoading(true);
      await preCheckData();
      await submitFormData();
      checkYGPGame();
    } catch (e) {
      callSetLoading(false);
    }
  };

  if (!isInit) return <PortalLoading />;

  return (
    <Spin spinning={isLoading}>
      <DrawerHeader
        title={intl.get(
          isAppendMode ? 'drawer_checkin_reverse_append_title' : 'drawer_checkin_reverse_title',
        )}
        onClose={handleEditCancel}
      />
      <div className={styles.pageContainer}>
        <div className={styles.noticeWrapper}>
          <Notice />
        </div>
        <div className={styles.mainContent}>
          <PopupTarget id={TargetType.CheckInByReserve} />
          <CustomerList isAppendMode={isAppendMode} />
          <RentTimeInfoNode />
          <ChannelInfoNode />
          <RoomSelector />
          <RemarkInfo />
        </div>
      </div>
      <DrawerFooter>
        <ThrottleButton type="primary" className={styles.submitBtn} onClick={handleSubmitClick}>
          {intl.get('drawer_checkin_create_submit_btn')}
        </ThrottleButton>
        <Button onClick={handleEditCancel}>{intl.get('app_confirm_cancel_btn')}</Button>
      </DrawerFooter>
      <PhoneCheckModal innerRef={phoneCheckModalRef} />
      <PreSettleDrawer ref={preSettleRef} />
    </Spin>
  );
};

export default () =>
  usePortalDrawer<IConfig>(connect(mapStateToProps, mapDispatchToProps)(memo(Index)));

// export default () => {
//   const { showDrawer } = usePortalDrawer(Index);
//   useEffect(() => {
//     showDrawer({
//       mainOrderId: 'Y20220112359392',
//       recList: ['J20220112729199'],
//     });
//   }, []);
//   return null;
// };
